import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import { RoundListNavItem, SeasonNavItem } from './nav-items';
import { RoundByRoundCards } from './round-by-round-cards';
import { roundByRoundNavSelector } from 'store/modules/brownlow-tracker/round-by-round/selectors';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useRoundByRoundNavSync } from './hooks/use-round-by-round-nav-sync';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { SettingsButton } from 'common/react/components/SettingsButton';
import { useWindowSizeV2 } from 'common/react/hooks/use-window-resize';
import { UPDATE_NAV_ITEM } from 'store/modules/brownlow-tracker/round-by-round/actions';
import { getTranslation } from 'common/react/utils/translations';

const NavWrapper = ({ children }) => (
    <div className="competition-nav filter-dropdown round-by-round">
        {children}
    </div>
);
NavWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

export const RoundByRound = ({
    seasonsToShow,
    seasonPid,
    competitionId,
    shouldHidePredicted,
    title
}) => {
    const nav = useSelector(roundByRoundNavSelector);
    const dispatch = useDispatch();
    const roundByRoundRef = useRef();
    const windowSize = useWindowSizeV2();

    const options = useCompetitionNavigationOptions(nav);

    options.defaultSeason = nav.season.providerId ?? seasonPid;
    options.seasonsToShow = seasonsToShow;

    const newOpts = {
        ...options,
        competitionId: competitionId
    };
    useRoundByRoundNavSync(newOpts);

    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: {
            season: nav.season,
            round: { roundNumber: nav.round }
        },
        options,
        filtersFields: ['season', 'round'],
        filtersRef: roundByRoundRef
    });

    const getInitialSeasonId = () => {
        if (seasonPid) {
            return options.seasons?.find(
                (season) => season.providerId === seasonPid
            )?.id;
        }
        return options.seasons?.[0]?.id;
    };

    const hasChangedFromDefaults = () => {
        if (!nav?.season || nav?.round === null) {
            return false;
        }

        const initialSeasonId = getInitialSeasonId();

        const isSeasonChanged = nav?.season?.id !== initialSeasonId;

        return isSeasonChanged;
    };

    const resetToDefaults = () => {
        const initialSeasonId = getInitialSeasonId();

        if (nav?.season?.id !== initialSeasonId) {
            const initialSeason = options.seasons?.find(
                (season) => season.id === initialSeasonId
            );
            if (initialSeason) {
                dispatch(
                    UPDATE_NAV_ITEM.request({
                        navItem: 'season',
                        value: {
                            id: initialSeason.id,
                            providerId: initialSeason.providerId
                        }
                    })
                );
            }
        }
    };

    return (
        <div ref={roundByRoundRef}>
            <StatsSection.Main NavWrapper={NavWrapper}>
                <StatsSection.Nav
                    showResponsiveItems={showResponsiveItems}
                    withScrollableFilter={!showResponsiveItems}
                >
                    <StatsSection.NavItems>
                        <SeasonNavItem
                            classes="season-nav-item"
                            seasonsToShow={seasonsToShow}
                            title={title}
                            isReadyToRender={windowSize.width !== null}
                        />
                        {hasChangedFromDefaults() && !showResponsiveItems ? (
                            <div className=" stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        ) : null}
                        {showResponsiveItems ? (
                            <StatsSection.SettingsButton classes="fixtures-nav__settings-button">
                                <SettingsButton />
                            </StatsSection.SettingsButton>
                        ) : null}
                    </StatsSection.NavItems>
                    {hasChangedFromDefaults() && showResponsiveItems ? (
                        <StatsSection.NavFilterItems>
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        </StatsSection.NavFilterItems>
                    ) : null}
                    <StatsSection.SettingsButton>
                        <SettingsButton />
                    </StatsSection.SettingsButton>
                    <StatsSection.RoundButtons>
                        <RoundListNavItem
                            classes={'competition-nav__round-filter-wrapper'}
                            title={title}
                        />
                    </StatsSection.RoundButtons>
                </StatsSection.Nav>
                <StatsSection.Content>
                    <StatsSection.ContentBody>
                        <RoundByRoundCards
                            shouldHidePredicted={shouldHidePredicted === 'true'}
                        />
                    </StatsSection.ContentBody>
                </StatsSection.Content>
            </StatsSection.Main>
        </div>
    );
};

RoundByRound.propTypes = {
    seasonsToShow: PropTypes.string,
    seasonPid: PropTypes.string,
    competitionId: PropTypes.string,
    shouldHidePredicted: PropTypes.string,
    title: PropTypes.string
};
